import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Typography, TextField, Alert } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAppGlass } from "./glass";
import { getAuthHeaders } from "./auth";
import { QUOTE_PAGE_TEMPLATE_OPTIONS, normalizeQuotePageTemplate } from "./quotePageTemplates";
import QuoteTemplateHero from "./QuoteTemplateHero";
import { DEFAULT_HOME_HERO, DEFAULT_BRANDING } from "./usePublicSiteSettings";

/** Mirrors server/lib/quoteSlug.js shape rules */
const SLUG_LEN_MIN = 3;
const SLUG_LEN_MAX = 40;
const SLUG_SHAPE_RE = /^[a-z0-9]+(?:-[a-z0-9]+){0,6}$/;

function isValidQuoteSlugClient(raw) {
  const t = (raw || "").trim().toLowerCase();
  return t.length >= SLUG_LEN_MIN && t.length <= SLUG_LEN_MAX && SLUG_SHAPE_RE.test(t);
}

/**
 * Admin: public quote URL, slug edit, landing template + preview (Dashboard → Website).
 */
export default function OperatorQuoteLinkPanel() {
  const g = useAppGlass();
  const [quoteSlug, setQuoteSlug] = useState("");
  const [quoteSlugDraft, setQuoteSlugDraft] = useState("");
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugMessage, setSlugMessage] = useState({ type: "", text: "" });
  const [savedQuoteTemplate, setSavedQuoteTemplate] = useState("classic");
  const [templateDraft, setTemplateDraft] = useState("classic");
  const [previewHero, setPreviewHero] = useState(DEFAULT_HOME_HERO);
  const [previewBranding, setPreviewBranding] = useState(DEFAULT_BRANDING);
  const [operatorPreviewName, setOperatorPreviewName] = useState("");
  const [templateMessage, setTemplateMessage] = useState({ type: "", text: "" });
  const [templateSaving, setTemplateSaving] = useState(false);
  /** @type {"idle"|"same"|"checking"|"available"|"taken"|"invalid"} */
  const [slugAvailability, setSlugAvailability] = useState("idle");
  const slugCheckTimerRef = useRef(null);
  const slugCheckGenRef = useRef(0);

  const loadQuoteIdentity = useCallback(async () => {
    try {
      const [userRes, pubRes] = await Promise.all([
        axios.get("/user/settings", { headers: getAuthHeaders() }),
        axios.get("/site-settings/public"),
      ]);
      const data = userRes.data;
      const s = typeof data.quoteSlug === "string" ? data.quoteSlug.trim().toLowerCase() : "";
      setQuoteSlug(s);
      setQuoteSlugDraft(s);
      const t = normalizeQuotePageTemplate(data.quotePageTemplate);
      setSavedQuoteTemplate(t);
      setTemplateDraft(t);

      const fn = typeof data.firstname === "string" ? data.firstname.trim() : "";
      const ln = typeof data.lastname === "string" ? data.lastname.trim() : "";
      setOperatorPreviewName([fn, ln].filter(Boolean).join(" ").trim());

      const pub = pubRes.data;
      const h = pub?.homeHero;
      if (h && typeof h === "object") {
        setPreviewHero({
          headline: typeof h.headline === "string" && h.headline ? h.headline : DEFAULT_HOME_HERO.headline,
          subheadline: typeof h.subheadline === "string" ? h.subheadline : DEFAULT_HOME_HERO.subheadline,
          imageUrl: typeof h.imageUrl === "string" ? h.imageUrl : "",
        });
      } else {
        setPreviewHero(DEFAULT_HOME_HERO);
      }
      const b = pub?.branding;
      if (b && typeof b === "object") {
        setPreviewBranding({
          primaryColor: typeof b.primaryColor === "string" ? b.primaryColor : DEFAULT_BRANDING.primaryColor,
          accentColor: typeof b.accentColor === "string" ? b.accentColor : DEFAULT_BRANDING.accentColor,
          showServiceCarousel: b.showServiceCarousel !== false,
          footerAttribution: typeof b.footerAttribution === "string" ? b.footerAttribution : "",
          logoUrl: typeof b.logoUrl === "string" ? b.logoUrl : "",
          hasStoredLogo: b.hasStoredLogo === true,
        });
      } else {
        setPreviewBranding(DEFAULT_BRANDING);
      }
    } catch {
      setQuoteSlug("");
      setQuoteSlugDraft("");
      setSavedQuoteTemplate("classic");
      setTemplateDraft("classic");
      setPreviewHero(DEFAULT_HOME_HERO);
      setPreviewBranding(DEFAULT_BRANDING);
      setOperatorPreviewName("");
    }
  }, []);

  useEffect(() => {
    loadQuoteIdentity();
  }, [loadQuoteIdentity]);

  useEffect(() => {
    if (slugCheckTimerRef.current) {
      clearTimeout(slugCheckTimerRef.current);
      slugCheckTimerRef.current = null;
    }
    const draft = quoteSlugDraft.trim().toLowerCase();
    if (!draft) {
      setSlugAvailability("idle");
      return;
    }
    const saved = (quoteSlug || "").trim().toLowerCase();
    if (draft === saved) {
      setSlugAvailability("same");
      return;
    }
    if (!isValidQuoteSlugClient(draft)) {
      setSlugAvailability("invalid");
      return;
    }
    setSlugAvailability("checking");
    const gen = (slugCheckGenRef.current += 1);
    slugCheckTimerRef.current = setTimeout(() => {
      slugCheckTimerRef.current = null;
      axios
        .get("/user/quote-slug-available", { headers: getAuthHeaders(), params: { q: draft } })
        .then(({ data }) => {
          if (gen !== slugCheckGenRef.current) return;
          if (data && data.available === true) {
            setSlugAvailability("available");
          } else {
            setSlugAvailability("taken");
          }
        })
        .catch(() => {
          if (gen !== slugCheckGenRef.current) return;
          setSlugAvailability("idle");
        });
    }, 420);
    return () => {
      if (slugCheckTimerRef.current) {
        clearTimeout(slugCheckTimerRef.current);
        slugCheckTimerRef.current = null;
      }
    };
  }, [quoteSlugDraft, quoteSlug]);

  const quotePageUrl = quoteSlug ? `${window.location.origin}/quote/${quoteSlug}` : "";

  const slugHelperText =
    slugAvailability === "invalid"
      ? `Use ${SLUG_LEN_MIN}–${SLUG_LEN_MAX} characters: lowercase letters, numbers, single hyphens (no leading/trailing or double hyphens).`
      : slugAvailability === "taken"
        ? "This ending is already taken by another account. Try a different one."
        : slugAvailability === "checking"
          ? "Checking availability…"
          : slugAvailability === "available"
            ? "This URL ending is available."
            : slugAvailability === "same"
              ? "This is your current link. Change the text above to pick a new ending."
              : `3–40 chars: lowercase letters, numbers, hyphens.`;

  const saveSlugDisabled =
    slugSaving ||
    !quoteSlugDraft.trim() ||
    slugAvailability === "checking" ||
    slugAvailability === "taken" ||
    slugAvailability === "invalid" ||
    slugAvailability === "same" ||
    slugAvailability === "idle";

  const copyQuoteLink = async () => {
    if (!quotePageUrl) return;
    try {
      await navigator.clipboard.writeText(quotePageUrl);
      setSlugMessage({ type: "success", text: "Link copied." });
    } catch {
      setSlugMessage({ type: "error", text: "Could not copy link." });
    }
  };

  const saveQuoteTemplateSelection = async () => {
    const want = normalizeQuotePageTemplate(templateDraft);
    setTemplateSaving(true);
    setTemplateMessage({ type: "", text: "" });
    try {
      await axios.patch("/user/quote-page-template", { quotePageTemplate: want }, { headers: getAuthHeaders() });
      setSavedQuoteTemplate(want);
      setTemplateMessage({
        type: "success",
        text: "Template saved. Open your quote link to see the full page.",
      });
    } catch (err) {
      setTemplateMessage({
        type: "error",
        text: err.response?.data?.message || "Could not save template.",
      });
    } finally {
      setTemplateSaving(false);
    }
  };

  const saveSlug = async () => {
    setSlugSaving(true);
    setSlugMessage({ type: "", text: "" });
    try {
      const next = quoteSlugDraft.trim().toLowerCase();
      await axios.patch("/user/quote-slug", { quoteSlug: next }, { headers: getAuthHeaders() });
      setQuoteSlug(next);
      setSlugAvailability("same");
      setSlugMessage({ type: "success", text: "Your quote link was updated." });
    } catch (err) {
      setSlugMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update link.",
      });
    } finally {
      setSlugSaving(false);
    }
  };

  return (
    <Box
      sx={{
        ...g.panel,
        borderRadius: 3,
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography variant="h6" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
        Your customer quote link
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        Share this URL with homeowners. It shows only the services on your <strong>Catalog</strong> tab (not the generic site
        catalog).
      </Typography>
      {quotePageUrl ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            value={quotePageUrl}
            fullWidth
            sx={{ flex: "1 1 280px", ...g.textField }}
            InputProps={{ readOnly: true }}
          />
          <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyQuoteLink}>
            Copy
          </Button>
          <Button
            component={RouterLink}
            to={quoteSlug ? `/quote/${quoteSlug}` : "/quote"}
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            endIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
          >
            Open
          </Button>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading your link…
        </Typography>
      )}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Customize URL ending
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start", mb: 1 }}>
        <Typography variant="body2" sx={{ pt: 1, color: "text.secondary" }}>
          …/quote/
        </Typography>
        <TextField
          size="small"
          value={quoteSlugDraft}
          onChange={(e) => setQuoteSlugDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
          placeholder="your-business"
          sx={{ width: 220, ...g.textField }}
          error={slugAvailability === "invalid" || slugAvailability === "taken"}
          helperText={slugHelperText}
        />
        <Button variant="contained" onClick={saveSlug} disabled={saveSlugDisabled}>
          {slugSaving ? "Saving…" : "Save"}
        </Button>
      </Box>
      {slugMessage.text ? (
        <Alert
          severity={slugMessage.type === "success" ? "success" : "error"}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setSlugMessage({ type: "", text: "" })}
        >
          {slugMessage.text}
        </Alert>
      ) : null}

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, mt: 2 }}>
        Quote landing style
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
        Click a style to preview it below. Hero text and colors match the sections above; your catalog still powers
        services. Save when you are ready to apply.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
          gap: 1,
          mb: 1,
        }}
      >
        {QUOTE_PAGE_TEMPLATE_OPTIONS.map((opt) => {
          const selected = templateDraft === opt.id;
          return (
            <Button
              key={opt.id}
              size="small"
              variant={selected ? "contained" : "outlined"}
              onClick={() => setTemplateDraft(opt.id)}
              disabled={templateSaving}
              sx={{
                py: 1,
                flexDirection: "column",
                alignItems: "stretch",
                textAlign: "left",
                borderWidth: selected ? 2 : 1,
                borderColor: selected ? "primary.main" : undefined,
              }}
            >
              <Typography variant="body2" fontWeight={700} component="span" sx={{ lineHeight: 1.3 }}>
                {opt.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 560, lineHeight: 1.5 }}>
        {QUOTE_PAGE_TEMPLATE_OPTIONS.find((o) => o.id === templateDraft)?.description ?? ""}
      </Typography>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Live preview
      </Typography>
      <Box
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
        }}
      >
        <QuoteTemplateHero
          templateId={templateDraft}
          homeHero={previewHero}
          primaryColor={previewBranding.primaryColor}
          accentColor={previewBranding.accentColor}
          operatorDisplayName={operatorPreviewName}
          compact
        />
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mt: 2 }}>
        <Button
          variant="contained"
          onClick={saveQuoteTemplateSelection}
          disabled={templateSaving || templateDraft === savedQuoteTemplate}
        >
          {templateSaving ? "Saving…" : "Save template"}
        </Button>
        <Typography variant="body2" color={templateDraft === savedQuoteTemplate ? "text.secondary" : "warning.main"}>
          {templateDraft === savedQuoteTemplate ? "All changes saved." : "You have unsaved changes."}
        </Typography>
      </Box>
      {templateMessage.text ? (
        <Alert
          severity={templateMessage.type === "success" ? "success" : "error"}
          sx={{ mt: 2, borderRadius: 2 }}
          onClose={() => setTemplateMessage({ type: "", text: "" })}
        >
          {templateMessage.text}
        </Alert>
      ) : null}
    </Box>
  );
}
